import { Order, AllocatedInventoryItem } from '../types/order';
import { CompletedProduct } from '../types/todo';

/**
 * Calculates order fulfillment status based on available inventory
 *
 * Algorithm:
 * 1. Process orders from oldest to newest (by orderDate)
 * 2. For each order, reserve quantities for ALL items (maintains priority)
 * 3. Check if order can be fulfilled from unreserved inventory
 * 4. Mark orders as ready if all items available after accounting for reservations
 *
 * @param orders - All orders in the system
 * @param completedProducts - Current inventory
 * @returns Updated orders with isReady and allocatedInventory fields
 */
export function calculateOrderFulfillment(orders: Order[], completedProducts: CompletedProduct[]): Order[] {
  // Sort orders by orderDate (oldest first) for processing
  const sortedOrders = [...orders].sort((a, b) => new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime());

  // Calculate total inventory per product
  const totalInventory = new Map<string, number>();
  completedProducts.forEach(cp => {
    const current = totalInventory.get(cp.productName) || 0;
    totalInventory.set(cp.productName, current + cp.unitCount);
  });

  // Track reserved quantities per SKU (cumulative from all older orders)
  const reservedQuantities = new Map<string, number>();

  // Process each order
  const updatedOrders = sortedOrders.map(order => {
    // Reset fulfillment status
    const updatedOrder: Order = {
      ...order,
      isReady: false,
      allocatedInventory: undefined
    };

    // Skip if order is already completed
    if (order.status === 'Completed') {
      return updatedOrder;
    }

    // Skip if order is not finalized
    if (!order.isFinalized) {
      return updatedOrder;
    }

    // Check if all items can be fulfilled from unreserved inventory
    const canFulfill = order.items.every(item => {
      const total = totalInventory.get(item.recipeName) || 0;
      const reserved = reservedQuantities.get(item.recipeName) || 0;
      const available = total - reserved;
      return available >= item.quantity;
    });

    // Reserve quantities for this order (regardless of fulfillment status)
    // This maintains priority for older orders
    order.items.forEach(item => {
      const currentReserved = reservedQuantities.get(item.recipeName) || 0;
      reservedQuantities.set(item.recipeName, currentReserved + item.quantity);
    });

    // Mark as ready if can fulfill
    if (canFulfill) {
      const allocated: AllocatedInventoryItem[] = order.items.map(item => ({
        productName: item.recipeName,
        quantity: item.quantity
      }));

      return {
        ...updatedOrder,
        isReady: true,
        allocatedInventory: allocated
      };
    }

    return updatedOrder;
  });

  // Return orders maintaining their original order (not sorted by date)
  return orders.map(order => {
    const updated = updatedOrders.find(u => u.id === order.id);
    return updated || order;
  });
}

/**
 * Calculates days since order date
 */
export function getDaysSinceOrder(orderDate: string): number {
  if (!orderDate) return 0;

  const [year, month, day] = orderDate.split('-').map(Number);
  const orderDateTime = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - orderDateTime.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Gets color class based on order age
 */
export function getOrderAgeColorClass(days: number, status: string): string {
  // Completed orders get gray
  if (status === 'Completed') {
    return 'order-age-completed';
  }

  // Age-based colors
  if (days <= 1) return 'order-age-fresh'; // Green
  if (days <= 3) return 'order-age-recent'; // Yellow-Green
  if (days <= 5) return 'order-age-warning'; // Yellow
  if (days === 6) return 'order-age-urgent'; // Orange
  return 'order-age-overdue'; // Red (7+)
}

/**
 * Formats date to readable string
 */
export function formatOrderDate(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Gets status display color
 */
export function getStatusColor(status: string): string {
  switch (status) {
    case 'Pending':
      return '#FF9800'; // Orange
    case 'In Progress':
      return '#2196F3'; // Blue
    case 'Completed':
      return '#4CAF50'; // Green
    default:
      return '#757575'; // Gray
  }
}

/**
 * Gets background color for order row based on age
 * Returns subtle colors (10-15% opacity) for readability
 */
export function getOrderRowBackgroundColor(days: number, status: string): string {
  // Completed orders stay white/no background color
  if (status === 'Completed') {
    return 'white';
  }

  // Age-based background colors (using rgba for 10-15% opacity)
  if (days <= 2) return 'rgba(76, 175, 80, 0.12)'; // Green (0-2 days)
  if (days <= 4) return 'rgba(255, 235, 59, 0.15)'; // Yellow (3-4 days)
  if (days <= 6) return 'rgba(255, 152, 0, 0.15)'; // Orange (5-6 days)
  return 'rgba(244, 67, 54, 0.15)'; // Red (7+ days)
}

/**
 * Gets the date range for the current week (Monday through Sunday)
 */
export function getCurrentWeekRange(): { start: Date; end: Date } {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate offset to Monday (1)
  // If Sunday (0), go back 6 days; otherwise go back (dayOfWeek - 1) days
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Calculate Monday of current week
  const monday = new Date(now);
  monday.setDate(now.getDate() - daysToMonday);
  monday.setHours(0, 0, 0, 0);

  // Calculate Sunday of current week
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { start: monday, end: sunday };
}

/**
 * Counts orders received within the current week (Monday through Sunday)
 */
export function getWeeklyOrderCount(orders: Order[]): number {
  const { start, end } = getCurrentWeekRange();

  return orders.filter(order => {
    const orderDate = new Date(order.orderDate);
    return orderDate >= start && orderDate <= end;
  }).length;
}

/**
 * Counts completed orders within the current week (Monday through Sunday)
 * Only counts orders with status 'Completed' and a completionDate within the current week
 */
export function getWeeklyCompletedOrderCount(orders: Order[]): number {
  const { start, end } = getCurrentWeekRange();

  return orders.filter(order => {
    // Must have status 'Completed' and a completionDate
    if (order.status !== 'Completed' || !order.completionDate) {
      return false;
    }

    const completionDate = new Date(order.completionDate);
    return completionDate >= start && completionDate <= end;
  }).length;
}

/**
 * Counts pending orders received within the current week (Monday through Sunday)
 */
export function getWeeklyPendingOrderCount(orders: Order[]): number {
  const { start, end } = getCurrentWeekRange();

  return orders.filter(order => {
    if (order.status !== 'Pending') {
      return false;
    }

    const orderDate = new Date(order.orderDate);
    return orderDate >= start && orderDate <= end;
  }).length;
}

/**
 * Counts in-progress orders received within the current week (Monday through Sunday)
 */
export function getWeeklyInProgressOrderCount(orders: Order[]): number {
  const { start, end } = getCurrentWeekRange();

  return orders.filter(order => {
    if (order.status !== 'In Progress') {
      return false;
    }

    const orderDate = new Date(order.orderDate);
    return orderDate >= start && orderDate <= end;
  }).length;
}
